<template>
  <section class="article-comments" aria-labelledby="article-comments-title">
    <header class="comments-header">
      <div>
        <p class="comments-eyebrow">DISCUSSION</p>
        <h2 id="article-comments-title">
          Comments
          <span class="comments-count">{{ total }}</span>
        </h2>
      </div>
    </header>

    <form class="comment-form" @submit.prevent="submitComment">
      <div v-if="replyTo" class="reply-context">
        <span>回复 {{ replyTo.nickname }}</span>
        <button type="button" @click="cancelReply">取消</button>
      </div>

      <div class="comment-identity">
        <label>
          <span>昵称</span>
          <input
            v-model.trim="form.nickname"
            type="text"
            maxlength="40"
            autocomplete="nickname"
            required
            placeholder="如何称呼你"
          >
        </label>
        <label>
          <span>邮箱（不公开）</span>
          <input
            v-model.trim="form.email"
            type="email"
            maxlength="254"
            autocomplete="email"
            placeholder="选填"
          >
        </label>
      </div>

      <label class="comment-body-field">
        <span class="sr-only">评论内容</span>
        <textarea
          ref="commentBody"
          v-model.trim="form.body"
          minlength="2"
          maxlength="1000"
          required
          placeholder="写下你的想法…"
        ></textarea>
      </label>

      <label class="comment-honeypot" aria-hidden="true">
        <span>Website</span>
        <input v-model="form.website" type="text" tabindex="-1" autocomplete="off">
      </label>

      <div class="comment-form-footer">
        <p>首次评论将在审核后显示 · 最多 1000 字</p>
        <button
          type="submit"
          :disabled="submitting || loading || !canSubmit"
        >
          {{ submitting ? '提交中…' : '提交评论' }}
        </button>
      </div>
      <p
        v-if="formMessage"
        class="form-message"
        :class="{ error: formError }"
        role="status"
      >
        {{ formMessage }}
      </p>
    </form>

    <div v-if="loading && comments.length === 0" class="comments-state">
      正在加载评论…
    </div>
    <div v-else-if="loadError && comments.length === 0" class="comments-state error">
      <p>{{ loadError }}</p>
      <button type="button" @click="loadComments(true)">重新加载</button>
    </div>
    <div v-else-if="comments.length === 0" class="comments-state">
      暂无评论，欢迎留下第一条想法。
    </div>

    <ol v-else class="comment-list">
      <li v-for="comment in comments" :key="comment.id" class="comment-thread">
        <article class="comment-card">
          <header>
            <span class="comment-avatar">{{ avatarLetter(comment.nickname) }}</span>
            <div>
              <strong>{{ comment.nickname }}</strong>
              <time :datetime="comment.created_at">{{ formatTime(comment.created_at) }}</time>
            </div>
          </header>
          <p class="comment-text">{{ comment.body }}</p>
          <button type="button" class="reply-button" @click="startReply(comment)">
            回复
          </button>
        </article>

        <ol v-if="comment.replies && comment.replies.length" class="reply-list">
          <li v-for="reply in comment.replies" :key="reply.id">
            <article class="comment-card reply-card">
              <header>
                <span class="comment-avatar">{{ avatarLetter(reply.nickname) }}</span>
                <div>
                  <strong>{{ reply.nickname }}</strong>
                  <time :datetime="reply.created_at">{{ formatTime(reply.created_at) }}</time>
                </div>
              </header>
              <p class="comment-text">{{ reply.body }}</p>
            </article>
          </li>
        </ol>
      </li>
    </ol>

    <button
      v-if="nextPage"
      type="button"
      class="load-more"
      :disabled="loading"
      @click="loadComments(false)"
    >
      {{ loading ? '加载中…' : '加载更多评论' }}
    </button>
  </section>
</template>

<script>
export default {
  name: 'article-comments',
  props: {
    postId: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      comments: [],
      total: 0,
      nextPage: 1,
      loading: false,
      loadError: '',
      submitting: false,
      formMessage: '',
      formError: false,
      replyTo: null,
      form: {
        nickname: '',
        email: '',
        body: '',
        website: ''
      }
    }
  },
  computed: {
    endpoint () {
      return `${this.$store.state.baseUrl}/blog/api/posts/${this.postId}/comments/`
    },
    canSubmit () {
      return this.form.nickname.length > 0 && this.form.body.length >= 2
    }
  },
  watch: {
    postId () {
      this.resetForPost()
    }
  },
  mounted () {
    this.restoreIdentity()
    this.loadComments(true)
  },
  methods: {
    responseBody (response) {
      if (response.body && typeof response.body === 'object') {
        return response.body
      }
      try {
        return JSON.parse(response.bodyText || '{}')
      } catch (error) {
        return {}
      }
    },
    csrfToken () {
      const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/)
      return match ? decodeURIComponent(match[1]) : ''
    },
    async loadComments (reset) {
      if (this.loading) return
      if (reset) {
        this.comments = []
        this.nextPage = 1
      }
      if (!this.nextPage) return
      this.loading = true
      this.loadError = ''
      try {
        const response = await this.$http.get(
          `${this.endpoint}?page=${this.nextPage}`
        )
        const data = this.responseBody(response)
        this.comments = this.comments.concat(data.comments || [])
        this.total = data.total || 0
        this.nextPage = data.next_page || null
      } catch (error) {
        this.loadError = '评论暂时无法加载，请稍后重试。'
      } finally {
        this.loading = false
      }
    },
    async submitComment () {
      if (!this.canSubmit || this.submitting) return
      this.submitting = true
      this.formMessage = ''
      this.formError = false
      try {
        const payload = {
          nickname: this.form.nickname,
          email: this.form.email,
          body: this.form.body,
          website: this.form.website,
          parent_id: this.replyTo ? this.replyTo.id : null
        }
        const response = await this.$http.post(this.endpoint, payload, {
          headers: {
            'X-CSRFToken': this.csrfToken()
          }
        })
        const data = this.responseBody(response)
        this.rememberIdentity()
        this.form.body = ''
        this.form.website = ''
        this.replyTo = null
        this.formMessage = data.message || '评论已提交'
        if (data.status === 'published') {
          await this.loadComments(true)
        }
      } catch (error) {
        const data = error && error.body ? error.body : {}
        this.formError = true
        this.formMessage = data.message || '提交失败，请稍后重试。'
      } finally {
        this.submitting = false
      }
    },
    startReply (comment) {
      this.replyTo = comment
      this.formMessage = ''
      this.$nextTick(() => {
        if (this.$refs.commentBody) {
          this.$refs.commentBody.focus()
        }
      })
    },
    cancelReply () {
      this.replyTo = null
    },
    resetForPost () {
      this.comments = []
      this.total = 0
      this.nextPage = 1
      this.replyTo = null
      this.form.body = ''
      this.formMessage = ''
      this.loadComments(true)
    },
    rememberIdentity () {
      try {
        window.localStorage.setItem('commentNickname', this.form.nickname)
        window.localStorage.setItem('commentEmail', this.form.email)
      } catch (error) {
        // Storage is optional; commenting still works when it is unavailable.
      }
    },
    restoreIdentity () {
      try {
        this.form.nickname = window.localStorage.getItem('commentNickname') || ''
        this.form.email = window.localStorage.getItem('commentEmail') || ''
      } catch (error) {
        // Ignore blocked storage.
      }
    },
    avatarLetter (nickname) {
      return (nickname || '?').trim().charAt(0).toUpperCase()
    },
    formatTime (value) {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    }
  }
}
</script>

<style scoped>
.article-comments {
  max-width: 980px;
  margin: 36px auto 72px;
  padding: 42px 0 0;
  border-top: 1px solid #dfe4e8;
  color: #27313a;
  font-size: 14px;
  font-family: 'Fira Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.comments-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 22px;
}

.comments-eyebrow {
  margin: 0 0 6px;
  color: #8a9199;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
}

.comments-header h2 {
  margin: 0;
  font-size: 23px;
  line-height: 1.2;
}

.comments-count {
  display: inline-flex;
  min-width: 26px;
  height: 23px;
  margin-left: 6px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #eef2f4;
  color: #69747d;
  font-size: 11px;
  vertical-align: middle;
}

.comment-form {
  position: relative;
  margin-bottom: 34px;
  padding: 18px;
  border: 1px solid #dfe4e8;
  border-radius: 10px;
  background: #fafbfb;
}

.comment-identity {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
}

.comment-identity label,
.comment-body-field {
  display: block;
}

.comment-identity span {
  display: block;
  margin-bottom: 6px;
  color: #69747d;
  font-size: 12px;
}

.comment-identity input,
.comment-body-field textarea {
  width: 100%;
  border: 1px solid #ccd3d8;
  border-radius: 6px;
  background: #fff;
  box-sizing: border-box;
  color: #27313a;
  font: inherit;
  outline: none;
}

.comment-identity input {
  height: 38px;
  padding: 0 12px;
  font-size: 13px;
}

.comment-body-field textarea {
  min-height: 96px;
  padding: 12px;
  font-size: 13px;
  line-height: 1.7;
  resize: vertical;
}

.comment-identity input:focus,
.comment-body-field textarea:focus {
  border-color: #4d6575;
  box-shadow: 0 0 0 3px rgba(77, 101, 117, 0.1);
}

.comment-form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 14px;
}

.comment-form-footer p {
  margin: 0;
  color: #8a9199;
  font-size: 12px;
}

.comment-form-footer button,
.load-more,
.comments-state button {
  border: 1px solid #344c5c;
  border-radius: 999px;
  background: #344c5c;
  color: #fff;
  cursor: pointer;
  font: inherit;
}

.comment-form-footer button {
  min-width: 108px;
  padding: 8px 16px;
  font-size: 13px;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.reply-context {
  display: flex;
  justify-content: space-between;
  margin: -4px 0 14px;
  padding: 8px 10px;
  border-left: 3px solid #8ca3b1;
  background: #eef3f5;
  color: #52636e;
  font-size: 13px;
}

.reply-context button,
.reply-button {
  padding: 0;
  border: 0;
  background: transparent;
  color: #607d8d;
  cursor: pointer;
  font: inherit;
}

.form-message {
  margin: 12px 0 0;
  color: #2f7d52;
  font-size: 13px;
}

.form-message.error,
.comments-state.error {
  color: #b33a3a;
}

.comment-list,
.reply-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.comment-thread {
  padding: 22px 0;
  border-bottom: 1px solid #edf0f2;
}

.comment-card header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.comment-avatar {
  display: inline-flex;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #e8eef1;
  color: #435a68;
  font-size: 14px;
  font-weight: 700;
}

.comment-card strong,
.comment-card time {
  display: block;
}

.comment-card strong {
  font-size: 14px;
}

.comment-card time {
  margin-top: 2px;
  color: #9099a0;
  font-size: 11px;
}

.comment-text {
  margin: 12px 0 8px 46px;
  color: #39454e;
  font-size: 13px;
  line-height: 1.75;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.reply-button {
  margin-left: 46px;
  font-size: 12px;
}

.reply-list {
  margin: 16px 0 0 46px;
  padding-left: 18px;
  border-left: 2px solid #edf0f2;
}

.reply-list li + li {
  margin-top: 18px;
}

.reply-card .comment-avatar {
  width: 30px;
  height: 30px;
  flex-basis: 30px;
  font-size: 12px;
}

.reply-card .comment-text {
  margin-left: 40px;
}

.comments-state {
  padding: 34px 18px;
  color: #8a9199;
  text-align: center;
}

.comments-state p {
  margin: 0 0 12px;
}

.comments-state button {
  padding: 7px 14px;
}

.load-more {
  display: block;
  margin: 24px auto 0;
  padding: 9px 18px;
  background: #fff;
  color: #344c5c;
}

.comment-honeypot {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

@media (max-width: 760px) {
  .article-comments {
    margin: 24px auto 52px;
    padding-top: 30px;
  }

  .comment-form {
    padding: 16px;
  }

  .comment-identity {
    grid-template-columns: 1fr;
  }

  .comment-form-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .comment-form-footer button {
    width: 100%;
  }

  .reply-list {
    margin-left: 18px;
    padding-left: 12px;
  }
}
</style>
